import { api } from '@/lib/api';
import { IAM_BASE } from '@/api/paths';
import { MEMBER_TYPES, type MemberType } from '@/auth/types';

/**
 * IAM ORGANIZASYON UCLARI — tipli sarmalayicilar + HAM -> GORUNUM donusumu.
 *
 * Neden `src/api/iam.ts` degil de feature icinde: o dosya iskeletin ORTAK
 * yuzeyi ve baska feature'lar da ona yaziyor. Organizasyon uclari yalniz bu
 * feature tarafindan kullaniliyor; burada durmalari, ortak dosyayi her yeni
 * ekranda buyuten ve catistiran bir deseni engelliyor.
 *
 * ZARF SOZLESMESI: her uc `{ code: 'success' | 'error', message, ... }` doner ve
 * HTTP 200 + `code: 'error'` MUMKUN. Yalniz HTTP durumuna bakan bir cagri
 * sessizce basarili sanir; bu yuzden `ensureSuccess` her yanittan geciyor.
 *
 * ORGANIZASYON KIMLIGI GONDERILMEZ. Sunucu onu cagiranin token'indan cozer
 * (iam-service.js "organizasyon uclari" blogu). Govdeye bir organizationId
 * eklemek, sunucunun yok sayacagi — ve saysaydi guvenlik acigi olacak — bir
 * alan eklemek olurdu.
 */

type Envelope = { code?: string; message?: string };

const ensureSuccess = <T extends Envelope>(data: T): T => {
  // Sunucu mesaji bir KODDUR (LAST_ADMIN_PROTECTED gibi); kullaniciya
  // gosterilecek metne cevirisi error-message.ts'te yapilir.
  if (data.code !== 'success') throw new Error(data.message ?? 'UNKNOWN_ERROR');
  return data;
};

/**
 * BEKLENMEYEN SEKIL SESSIZCE BOS LISTEYE DUSMEZ.
 *
 * `?? []` yazmak cazip ama tehlikeli: alan adi degisirse (ornegin `memberList`
 * -> `items`) ekran hatasiz calisir ve "ekipte kimse yok" der. Ne tsc ne de
 * test bunu yakalar — sekil yalniz calisma zamaninda belli olur. Bu yuzden
 * eksik/yanlis bir liste alani HATA'dir; arayuz hata durumunu zaten cizebiliyor.
 *
 * NOT: IAM'in uye/hesap uclari `{ items, page }` sozlesmesini KULLANMIYOR
 * (`{ code, memberList }` donerler). O sozlesme currency servisinin
 * tasks/offers uclarinda ve BURADA yalniz audit-log'da gecerli.
 */
const requireArray = (value: unknown, field: string): unknown[] => {
  if (!Array.isArray(value)) throw new Error(`MALFORMED_RESPONSE:${field}`);
  return value;
};

/**
 * pg int8'i STRING dondurur; `id`, `memberId`, `appUserId`, `organizationId`
 * HER YERDE string. Number()'a cevirip === ile karsilastirmak 2^53 ustunde
 * sessizce yanlis sonuc verir — kimlikler asla sayiya cevrilmez.
 */
const asId = (value: unknown): string => {
  if (typeof value === 'string') return value;
  // Sunucu int8'i string doner; number yalniz kucuk kimliklerde gorulur.
  if (typeof value === 'number' || typeof value === 'bigint') return String(value);
  return '';
};

/**
 * SAYAC alanlari (memberCount, permissionCount) `count(*)` sonucu, yani yine
 * bigint ve yine STRING. Bunlarda Number() cast'i BILINCLI ve guvenli: bir
 * sayac kimlik degil ve 2^53 sinirina yaklasmasi mumkun degil.
 */
const asCount = (value: unknown): number => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const asMemberType = (value: unknown): MemberType | null =>
  MEMBER_TYPES.includes(value as MemberType) ? (value as MemberType) : null;

const asText = (value: unknown): string => (typeof value === 'string' ? value : '');

/* -------------------------------------------------------------------------- */
/* Organizasyon profili                                                        */
/* -------------------------------------------------------------------------- */

/**
 * Sunucunun GUNCELLEMEYE IZIN VERDIGI alanlar (iam-service.js postOrganizationMe
 * beyaz listesi ile BIREBIR). Listede olmayan bir alan gonderilirse sessizce
 * yok sayilir — form bu yuzden bu diziden uretiliyor, elle yazilmiyor.
 *
 * `labelKey` metin DEGIL ceviri anahtari: dizi modul seviyesinde, t() burada
 * cagrilamaz (cagrilsaydi dil degisiminde bayat kalirdi).
 */
export const ORGANIZATION_FIELDS = [
  { key: 'organizationName', labelKey: 'org.profile.name' },
  { key: 'organizationEmail', labelKey: 'org.profile.email' },
  { key: 'organizationPhone', labelKey: 'org.profile.phone' },
  { key: 'organizationAddress', labelKey: 'org.profile.address' },
  { key: 'organizationCity', labelKey: 'org.profile.city' },
  { key: 'organizationDistrict', labelKey: 'org.profile.district' },
  { key: 'businessChamber', labelKey: 'org.profile.chamber' },
  { key: 'businessNumber', labelKey: 'org.profile.chamberNo' },
] as const;

export type OrganizationFieldKey = (typeof ORGANIZATION_FIELDS)[number]['key'];

export type OrganizationProfile = {
  id: string;
  memberCount: number;
  /** Beyaz listedeki alanlar; sunucuda NULL olabilir, formda bos dize olur. */
  fields: Record<OrganizationFieldKey, string>;
};

export type OrganizationContext = {
  organization: OrganizationProfile;
  memberType: MemberType | null;
  /** Cagiranin ETKIN yetkileri (yoneticide sunucu ortuk yetkileri de acar). */
  permissions: readonly string[];
};

type RawOrganization = Record<string, unknown> & { id?: unknown; memberCount?: unknown };

const toOrganizationProfile = (raw: RawOrganization): OrganizationProfile => {
  const fields = {} as Record<OrganizationFieldKey, string>;
  for (const field of ORGANIZATION_FIELDS) fields[field.key] = asText(raw[field.key]);
  return { id: asId(raw.id), memberCount: asCount(raw.memberCount), fields };
};

type OrganizationMeResponse = Envelope & {
  organization?: RawOrganization;
  membership?: { memberType?: unknown; permissions?: unknown };
};

export const fetchOrganization = async (): Promise<OrganizationContext> => {
  const { data } = await api.get<OrganizationMeResponse>(`${IAM_BASE}/v1/organizations/me`);
  ensureSuccess(data);
  return {
    organization: toOrganizationProfile(data.organization ?? {}),
    memberType: asMemberType(data.membership?.memberType),
    permissions: Array.isArray(data.membership?.permissions)
      ? (data.membership.permissions as string[])
      : [],
  };
};

/** Yalniz buro YONETICISI. Sunucu degisikligi denetim kaydina yazar. */
export const updateOrganization = async (
  fields: Record<OrganizationFieldKey, string>,
): Promise<OrganizationProfile> => {
  const { data } = await api.post<OrganizationMeResponse>(
    `${IAM_BASE}/v1/organizations/me`,
    fields,
  );
  ensureSuccess(data);
  return toOrganizationProfile(data.organization ?? {});
};

/* -------------------------------------------------------------------------- */
/* Uyeler                                                                      */
/* -------------------------------------------------------------------------- */

export type OrganizationMember = {
  /** ORGANIZASYON UYELIGI kimligi — uye uclarinin bekledigi id budur. */
  id: string;
  /** KULLANICI kimligi. "Bu satir ben miyim" karsilastirmasi bununla yapilir. */
  appUserId: string;
  memberType: MemberType | null;
  isActive: boolean;
  userFullName: string;
  userEmail: string;
  permissionCount: number;
};

type RawMember = Record<string, unknown>;

const toMember = (raw: RawMember): OrganizationMember => ({
  id: asId(raw.id),
  appUserId: asId(raw.appUserId),
  memberType: asMemberType(raw.organizationMemberTypeCode),
  isActive: raw.isActive !== false,
  userFullName: asText(raw.userFullName),
  userEmail: asText(raw.userEmail),
  permissionCount: asCount(raw.permissionCount),
});

export const fetchMembers = async (): Promise<OrganizationMember[]> => {
  const { data } = await api.get<Envelope & { memberList?: RawMember[] }>(
    `${IAM_BASE}/v1/organizations/me/members`,
  );
  ensureSuccess(data);
  // Alan adi `memberList` — DOGRULANDI (canli servis). `items` DEGIL.
  return (requireArray(data.memberList, 'memberList') as RawMember[]).map(toMember);
};

/**
 * Uye tipini degistirir. Sunucu iki seyi REDDEDER ve ikisi de bilincli:
 * kendi uyeligini degistirmek (CANNOT_MODIFY_OWN_MEMBERSHIP) ve son yoneticiyi
 * dusurmek (LAST_ADMIN_PROTECTED). Ayrica o uyenin jetonlarini iptal eder.
 */
export const changeMemberType = async (input: {
  memberId: string;
  memberTypeCode: MemberType;
}): Promise<void> => {
  const { data } = await api.post<Envelope>(`${IAM_BASE}/v1/organizations/me/members`, input);
  ensureSuccess(data);
};

/** Yumusak cikarma (is_active=false) + o uyenin jetonlarinin iptali. */
export const removeMember = async (input: { memberId: string }): Promise<void> => {
  const { data } = await api.post<Envelope>(
    `${IAM_BASE}/v1/organizations/me/members/delete`,
    input,
  );
  ensureSuccess(data);
};

/* -------------------------------------------------------------------------- */
/* Yetki katalogu ve uye yetkileri                                             */
/* -------------------------------------------------------------------------- */

export type PermissionCatalogueItem = {
  code: string;
  /** operation | finance | report | management — ekranda bolum basligi. */
  group: string;
  /** Sunucudan gelen Turkce aciklama; yoksa yerel etiket kullanilir. */
  descriptionTr: string;
};

/**
 * VERILEBILIR kodlarin katalogu — cagiranin KENDI yetkileri DEGIL.
 * Organizasyon baglami yoksa 403 doner (musteri hesabinda hic cagrilmaz).
 *
 * Yonetici yetkileri (manageMembers, managePermissions, editOrganization,
 * viewAuditLog) sunucuda `is_org_grantable=false` oldugu icin bu listede HIC
 * yer almaz; ekranda kutu olarak gorunmemelerinin sebebi de budur.
 */
export const fetchPermissionCatalogue = async (): Promise<PermissionCatalogueItem[]> => {
  const { data } = await api.get<Envelope & { permissionList?: RawMember[] }>(
    `${IAM_BASE}/v1/permissions`,
  );
  ensureSuccess(data);
  return (requireArray(data.permissionList, 'permissionList') as RawMember[]).map((raw) => ({
    code: asText(raw.code),
    group: asText(raw.permissionGroup) || 'other',
    descriptionTr: asText(raw.descriptionTr),
  }));
};

export const fetchMemberPermissions = async (memberId: string): Promise<string[]> => {
  const { data } = await api.get<Envelope & { permissionCodes?: unknown }>(
    `${IAM_BASE}/v1/organizations/me/members/permissions`,
    { params: { memberId } },
  );
  ensureSuccess(data);
  return requireArray(data.permissionCodes, 'permissionCodes') as string[];
};

/**
 * YETKI SETI BUTUN OLARAK DEGISTIRILIR (replace, delta DEGIL).
 *
 * Sunucu once uyenin tum grant'lerini pasifleştirir, sonra GONDERILENLERI
 * yazar. Yani eksik gonderilen her kod SESSIZCE GERI ALINIR. Ekran bu yuzden
 * yerel bir taslak tutar ve TAM SETI gonderir; "yalniz degisenleri gonder"
 * turunden bir iyilestirme, kaydeden kisinin gormedigi bir yetki kaybi uretir.
 *
 * Sunucu ayrica o uyenin jetonlarini iptal eder — degisiklik aninda etkir.
 */
export const replaceMemberPermissions = async (input: {
  memberId: string;
  permissionCodes: readonly string[];
}): Promise<void> => {
  const { data } = await api.post<Envelope>(
    `${IAM_BASE}/v1/organizations/me/members/permissions`,
    { memberId: input.memberId, permissionCodes: [...input.permissionCodes] },
  );
  ensureSuccess(data);
};

/* -------------------------------------------------------------------------- */
/* Hesap acma (davet)                                                          */
/* -------------------------------------------------------------------------- */

export type CreatedMember = {
  emailDelivered: boolean;
  /**
   * YALNIZ e-posta GIDEMEDIGINDE gelir ve YALNIZ BIR KEZ gorunur; sunucu bir
   * daha vermez. Bu yuzden ekranda kapatilabilir bir kutuda degil, kalici bir
   * panelde ve hukuki bilgilendirmeyle birlikte gosterilir.
   */
  tempPassword: string | null;
};

export const createMember = async (input: {
  invitedFullName: string;
  invitedEmail: string;
  memberTypeCode: MemberType;
  /** Yonetici icin BOS gonderilir; sunucu admin + yetki listesini reddeder. */
  initialPermissions: readonly string[];
}): Promise<CreatedMember> => {
  const { data } = await api.post<Envelope & { emailDelivered?: unknown; tempPassword?: unknown }>(
    `${IAM_BASE}/v1/organizations/me/members/create`,
    {
      invitedFullName: input.invitedFullName,
      invitedEmail: input.invitedEmail,
      memberTypeCode: input.memberTypeCode,
      initialPermissions: [...input.initialPermissions],
    },
  );
  ensureSuccess(data);
  return {
    emailDelivered: data.emailDelivered === true,
    tempPassword: typeof data.tempPassword === 'string' ? data.tempPassword : null,
  };
};

/* -------------------------------------------------------------------------- */
/* Denetim kaydi                                                               */
/* -------------------------------------------------------------------------- */

export type AuditLogEntry = {
  id: string;
  actionCode: string;
  actorName: string;
  createdAt: string;
  /** actionData.userEmail — uye islemlerinde hedefin e-postasi. */
  targetEmail: string;
  /** actionData.permissionCodes uzunlugu; yoksa null. */
  permissionCount: number | null;
};

/**
 * LISTE SOZLESMESI: `{ items, page }` ve `page = { limit, offset, returned,
 * hasMore, sort, snapshotAt }`. `total` YOK — "3 / 120" gibi bir sayac
 * cizilemez; sayfalama hasMore uzerinden yurur.
 */
export type PageMeta = {
  limit: number;
  offset: number;
  returned: number;
  hasMore: boolean;
  snapshotAt: string | null;
};

export type AuditLogPage = { items: AuditLogEntry[]; page: PageMeta };

export const AUDIT_PAGE_SIZE = 20;

type RawAuditEntry = Record<string, unknown> & {
  actionData?: { userEmail?: unknown; permissionCodes?: unknown } | null;
};

const toAuditEntry = (raw: RawAuditEntry): AuditLogEntry => {
  const codes = raw.actionData?.permissionCodes;
  return {
    id: asId(raw.id),
    actionCode: asText(raw.actionCode),
    actorName: asText(raw.actorName),
    createdAt: asText(raw.createdAt),
    targetEmail: asText(raw.actionData?.userEmail),
    permissionCount: Array.isArray(codes) ? codes.length : null,
  };
};

export const fetchAuditLog = async (input: {
  offset: number;
  /**
   * Ilk sayfanin cektigi an. Sonraki sayfalarda GERI GONDERILIR: aksi halde
   * kaydirma sirasinda yazilan yeni bir denetim kaydi pencereyi kaydirir ve
   * ayni satir iki sayfada birden gorunur.
   */
  snapshotAt: string | null;
  actionCode: string | null;
}): Promise<AuditLogPage> => {
  const { data } = await api.get<Envelope & { items?: RawAuditEntry[]; page?: Partial<PageMeta> }>(
    `${IAM_BASE}/v1/organizations/me/audit-log`,
    {
      params: {
        limit: AUDIT_PAGE_SIZE,
        offset: input.offset,
        ...(input.snapshotAt ? { snapshotAt: input.snapshotAt } : {}),
        ...(input.actionCode ? { actionCode: input.actionCode } : {}),
      },
    },
  );
  ensureSuccess(data);
  // Denetim kaydi, uye uclarinin AKSINE `{ items, page }` sozlesmesini kullanir
  // (iam-service.js getAuditLog -> buildPageMeta). Ikisi karistirilmamali.
  const items = (requireArray(data.items, 'items') as RawAuditEntry[]).map(toAuditEntry);
  return {
    items,
    page: {
      limit: asCount(data.page?.limit) || AUDIT_PAGE_SIZE,
      offset: asCount(data.page?.offset),
      returned: data.page?.returned === undefined ? items.length : asCount(data.page.returned),
      hasMore: data.page?.hasMore === true,
      snapshotAt: typeof data.page?.snapshotAt === 'string' ? data.page.snapshotAt : null,
    },
  };
};
